// MEDExplorerPanel — mini documents browser cho rightsidebar.
// Mục đích: DUYỆT (tree + folder nav) + MỞ (preview modal) + DRAG-DROP source vào phiếu.
// KHÔNG có edit: rename/delete/move/share/bulk → user vào /media/MEDExplorer.
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import AntAPI from '../../utils/AntAPI';
import AntLocalization from '../../utils/AntLocalization';
import { AntSidebar } from '../../utils/AntContext';
import AntFileViewer from '../AntFileViewer';
import { sidebarRelativeTime } from './panels/SidebarStandard';

function SvgIcon(props) {
    return (
        <svg width={props.size || 14} height={props.size || 14} viewBox="0 0 24 24"
             fill="none" stroke={props.color || 'currentColor'}
             strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d={props.d} />
        </svg>
    );
}

function AnalyzingSpinner(props) {
    var size = props.size || 12;
    return (
        <svg className="med-spinner" width={size} height={size} viewBox="0 0 50 50"
             style={{ animation: 'medSpinnerRotate 1s linear infinite', verticalAlign: 'middle' }}>
            <circle cx="25" cy="25" r="20" fill="none" stroke="#6687e2" strokeWidth="5" strokeDasharray="80 40" strokeLinecap="round" />
        </svg>
    );
}

function isAnalyzing(file) {
    return file && (file.ai_last_analysis_status === 'Running' || file.ai_last_analysis_status === 'Pending');
}

var ICONS = {
    folder: 'M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V7z',
    file: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
    image: 'M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z',
    search: 'M21 21l-4.35-4.35M10.5 17a6.5 6.5 0 100-13 6.5 6.5 0 000 13z',
    tree: 'M4 6h16M4 12h16M4 18h16',
    refresh: 'M23 4v6h-6M1 20v-6h6M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15',
    up: 'M5 15l7-7 7 7',
    chevronRight: 'M9 18l6-6-6-6',
    chevronDown: 'M6 9l6 6 6-6',
    myDrive: 'M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-7l-2-2H5a2 2 0 00-2 2z',
    shared: 'M17 8a3 3 0 110-6 3 3 0 010 6zM7 14a3 3 0 110-6 3 3 0 010 6zm10 6a3 3 0 110-6 3 3 0 010 6zM9.5 13.5l5-2.5m0 5l-5-2.5'
};

function formatSize(bytes) {
    if (!bytes) return '';
    var n = Number(bytes);
    if (!isFinite(n) || n <= 0) return '';
    if (n < 1024) return n + ' B';
    if (n < 1024 * 1024) return Math.round(n / 1024) + ' KB';
    return (n / 1024 / 1024).toFixed(1) + ' MB';
}

function formatTime(ts) {
    return sidebarRelativeTime(ts);
}

// ─── Skeuomorphic file/folder icons — ĐỒNG BỘ với MEDExplorer (Icons.js) ───
// Port nguyên màu + hình từ ante-web/pages/media/MEDExplorer/Icons.js (kit không import
// được từ ante-web). Folder cam-vàng, file giấy + badge màu theo loại.
var SKEUO_TYPE_META = {
    PDF:   { color: '#dc2626', label: 'PDF' },
    DOC:   { color: '#2563eb', label: 'DOC' },
    DOCX:  { color: '#2563eb', label: 'DOC' },
    XLS:   { color: '#059669', label: 'XLS' },
    XLSX:  { color: '#059669', label: 'XLS' },
    CSV:   { color: '#059669', label: 'CSV' },
    PPT:   { color: '#ea580c', label: 'PPT' },
    PPTX:  { color: '#ea580c', label: 'PPT' },
    IMAGE: { color: '#8b5cf6', label: 'IMG' },
    VIDEO: { color: '#db2777', label: 'VID' },
    AUDIO: { color: '#0891b2', label: 'AUD' },
    ZIP:   { color: '#475569', label: 'ZIP' },
    RAR:   { color: '#475569', label: 'RAR' },
    TXT:   { color: '#64748b', label: 'TXT' }
};
function skeuoMetaFor(type) {
    var key = String(type || '').toUpperCase();
    return SKEUO_TYPE_META[key] || { color: '#94a3b8', label: '' };
}

function SkeuoFolderIcon(props) {
    var size = props.size || 30;
    var uid = 'rsFld' + size;
    return (
        <svg width={size} height={size} viewBox="0 0 48 48" style={{ display: 'block' }} xmlns="http://www.w3.org/2000/svg">
            <defs>
                <linearGradient id={uid} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0" stopColor="#fcd34d" />
                    <stop offset="1" stopColor="#fbbf24" />
                </linearGradient>
            </defs>
            <path d="M4 12h14l4 4h22a2 2 0 012 2v2H2v-6a2 2 0 012-2z" fill="#ea580c" />
            <path d="M2 18h44v22a2 2 0 01-2 2H4a2 2 0 01-2-2V18z" fill={'url(#' + uid + ')'} />
            <path d="M2 18h44v1H2z" fill="#c2410c" opacity="0.3" />
        </svg>
    );
}

function SkeuoFileIcon(props) {
    var size = props.size || 30;
    var type = (props.type || '').toUpperCase();
    var meta = skeuoMetaFor(type);
    var isImage = type === 'IMAGE';
    var uid = 'rsFile' + size;
    return (
        <svg width={size} height={size} viewBox="0 0 48 48" style={{ display: 'block' }} xmlns="http://www.w3.org/2000/svg">
            <defs>
                <linearGradient id={uid} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0" stopColor="#ffffff" />
                    <stop offset="1" stopColor="#f1f5f9" />
                </linearGradient>
            </defs>
            <path d="M10 4h20l12 12v26a2 2 0 01-2 2H10a2 2 0 01-2-2V6a2 2 0 012-2z"
                  fill={'url(#' + uid + ')'} stroke="#cbd5e1" strokeWidth="1" />
            <path d="M30 4v10a2 2 0 002 2h10" fill="#e2e8f0" stroke="#cbd5e1" strokeWidth="1" />
            {isImage && (
                <g>
                    <rect x="10" y="20" width="28" height="14" rx="1" fill={meta.color} opacity="0.15" />
                    <circle cx="16" cy="25" r="2" fill={meta.color} opacity="0.6" />
                    <path d="M10 32l6-6 6 5 4-3 8 5v1H10z" fill={meta.color} opacity="0.45" />
                    <rect x="6" y="38" width="22" height="6" rx="1" fill={meta.color} />
                    <text x="17" y="42.5" fontSize="4.5" fontWeight="700" fill="#ffffff" textAnchor="middle" fontFamily="-apple-system, system-ui, sans-serif">IMG</text>
                </g>
            )}
            {meta.label && !isImage && (
                <g>
                    <rect x="6" y="30" width="22" height="11" rx="2" fill={meta.color} />
                    <text x="17" y="38.5" fontSize="7" fontWeight="700" fill="#ffffff" textAnchor="middle"
                          fontFamily="-apple-system, system-ui, sans-serif" style={{ letterSpacing: '0.5px' }}>{meta.label}</text>
                </g>
            )}
        </svg>
    );
}

function SkeuoIcon(props) {
    if (props.isFolder) return <SkeuoFolderIcon size={props.size} />;
    return <SkeuoFileIcon size={props.size} type={props.type} />;
}

// ─── Tree row (collapsible group) ─────────────────────────────────────
function TreeGroup(props) {
    var [open, setOpen] = useState(!!props.defaultOpen);
    var isActive = props.activeKey && props.selfKey === props.activeKey;
    return (
        <div className="med-tree-group">
            <div className={'med-tree-row' + (isActive ? ' active' : '')}
                 onClick={function() { props.onSelect && props.onSelect(); }}>
                {props.expandable && (
                    <span className="med-tree-caret" onClick={function(e) {
                        e.stopPropagation(); setOpen(!open);
                    }}>
                        <SvgIcon d={open ? ICONS.chevronDown : ICONS.chevronRight} size={10} />
                    </span>
                )}
                {!props.expandable && <span className="med-tree-caret-spacer" />}
                <SvgIcon d={props.icon} size={13} />
                <span className="med-tree-label">{props.label}</span>
                {typeof props.badge === 'number' && props.badge > 0 && (
                    <span className="med-tree-badge">{props.badge}</span>
                )}
            </div>
            {open && props.children && (
                <div className="med-tree-children">{props.children}</div>
            )}
        </div>
    );
}

function TreeLeaf(props) {
    var isActive = props.activeKey === props.selfKey;
    return (
        <div className={'med-tree-row leaf' + (isActive ? ' active' : '')}
             onClick={function() { props.onSelect && props.onSelect(); }}
             onDragOver={props.onDragOver}
             onDrop={props.onDrop}>
            <span className="med-tree-caret-spacer" />
            <SvgIcon d={props.icon || ICONS.folder} size={12} />
            <span className="med-tree-label">{props.label}</span>
            {typeof props.badge === 'number' && props.badge > 0 && (
                <span className="med-tree-badge">{props.badge}</span>
            )}
        </div>
    );
}

export default function MEDExplorerPanel(props) {
    var sidebarCtx = AntSidebar ? AntSidebar() : null;
    var pageContext = (sidebarCtx && sidebarCtx.context) || null;
    var voucherTypeFromPage = pageContext && pageContext.voucherType;

    // selectedNode shape thống nhất với full-page:
    //  { drive_kind: 'my'|'shared'|'voucher', code, label, is_folder_subnode, parent_folder_code, sharer_code }
    var initialNode = voucherTypeFromPage
        ? { drive_kind: 'voucher', code: voucherTypeFromPage, label: voucherTypeFromPage, is_folder_subnode: false }
        : { drive_kind: 'my', code: null, label: 'My Drive', is_folder_subnode: false };
    var [selectedNode, setSelectedNode] = useState(initialNode);
    var [showTree, setShowTree] = useState(false);  // drawer: mặc định ẨN, ≡ toggle
    var [voucherTypes, setVoucherTypes] = useState([]);
    var [searchQuery, setSearchQuery] = useState('');
    var [items, setItems] = useState([]);
    var [loading, setLoading] = useState(false);
    var [viewerFile, setViewerFile] = useState(null);
    var historyRef = useRef([]);  // stack of nodes — Up button uses it

    var API = useMemo(function() { return new AntAPI('media', 'MEDDocument'); }, []);

    var translate = function(obj) { return AntLocalization.translateText(obj); };

    // ─── Load voucher_types 1 lần khi mount ──────────────────────────
    useEffect(function() {
        API.post('/' + API.routeURL + '/actionListByVoucherType', {}, function(r) {
            var rows = (r && r.rows) || (r && r.data && r.data.rows) || [];
            setVoucherTypes(Array.isArray(rows) ? rows : []);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ─── Load items theo selectedNode ────────────────────────────────
    var loadItems = useCallback(function() {
        setLoading(true);
        var payload = {};
        var driveKind = selectedNode.drive_kind;
        if (driveKind === 'voucher') {
            payload.folder_view = 'voucher';
            payload.voucher_type = selectedNode.code;
        } else if (driveKind === 'shared') {
            payload.folder_view = 'shared';
        } else {
            payload.folder_view = 'folder';
            if (selectedNode.is_folder_subnode) payload.parent_folder_code = selectedNode.code;
        }
        if (searchQuery && searchQuery.trim()) payload.search = searchQuery.trim();
        API.post('/' + API.routeURL + '/actionListChildren', payload, function(r) {
            var rows;
            if (driveKind === 'shared') {
                var d = (r && r.data) ? r.data : r;
                var all = [];
                (d.groupedByOwner || []).forEach(function(g) { all = all.concat(g.items || []); });
                if ((d.publicItems || []).length > 0) {
                    d.publicItems.forEach(function(p) {
                        if (!all.find(function(x) { return x.id === p.id; })) all.push(p);
                    });
                }
                rows = all;
            } else {
                rows = (r && r.rows) || (r && r.data && r.data.rows) || [];
            }
            setItems(Array.isArray(rows) ? rows : []);
            setLoading(false);
        });
    }, [API, selectedNode.drive_kind, selectedNode.code, selectedNode.is_folder_subnode, searchQuery]);

    useEffect(function() { loadItems(); }, [loadItems]);

    // ─── Navigate helpers ────────────────────────────────────────────
    var navigateTo = function(node) {
        historyRef.current.push(selectedNode);
        setSelectedNode(node);
    };

    var canUp = selectedNode.drive_kind === 'my' && selectedNode.is_folder_subnode;
    var navUp = function() {
        if (!canUp) return;
        var parentCode = selectedNode.parent_folder_code || null;
        if (!parentCode) {
            setSelectedNode({ drive_kind: 'my', code: null, label: 'My Drive', is_folder_subnode: false });
            return;
        }
        // Lookup folder bằng /list (BE actionGetAll signature: payload as cond)
        API.post('/' + API.routeURL + '/list', { is_folder: 1, code: parentCode }, function(r) {
            var rows = (r && r.rows) || (r && r.data && r.data.rows) || [];
            if (rows.length > 0) {
                var f = rows[0];
                setSelectedNode({
                    drive_kind: 'my', code: f.code, label: f.name, is_folder_subnode: true,
                    parent_folder_code: f.parent_folder_code, folder_path: f.folder_path
                });
            } else {
                setSelectedNode({ drive_kind: 'my', code: null, label: 'My Drive', is_folder_subnode: false });
            }
        });
    };

    // ─── Row interactions ────────────────────────────────────────────
    var handleRowClick = function(item) {
        var isFolder = item.is_folder === 1 || item.is_folder === true;
        if (isFolder) {
            // Navigate vào folder con
            navigateTo({
                drive_kind: 'my', code: item.code, label: item.name, is_folder_subnode: true,
                parent_folder_code: item.parent_folder_code, folder_path: item.folder_path
            });
        } else {
            // Mở viewer modal
            if (item.full_path) setViewerFile(item);
        }
    };

    var handleDragStart = function(item) {
        return function(e) {
            var isFolder = item.is_folder === 1 || item.is_folder === true;
            if (isFolder) {
                // Folder không phải drag source vào voucher; skip
                e.preventDefault();
                return;
            }
            try {
                e.dataTransfer.setData('application/x-meddoc', JSON.stringify({
                    document_id: item.id, document_code: item.code,
                    document_name: item.name, document_type: item.type
                }));
                e.dataTransfer.effectAllowed = 'copyMove';
            } catch (err) {/* silent */}
        };
    };

    // ─── Breadcrumb label ────────────────────────────────────────────
    var breadcrumb;
    if (selectedNode.drive_kind === 'voucher') {
        var vt = voucherTypes.find(function(v) { return v.voucher_type === selectedNode.code; });
        breadcrumb = (vt && vt.label && translate(vt.label)) || selectedNode.code;
    } else if (selectedNode.drive_kind === 'shared') {
        breadcrumb = translate({ vi: 'Chia sẻ với tôi', en: 'Shared with me', fr: 'Partagés avec moi', es: 'Compartidos conmigo', id: 'Dibagikan kepada saya', ja: '共有アイテム', zh: '与我共享', ko: '나와 공유됨', hi: 'मेरे साथ साझा', ar: 'تمت مشاركتها معي' });
    } else if (selectedNode.is_folder_subnode) {
        breadcrumb = selectedNode.folder_path || selectedNode.label;
    } else {
        breadcrumb = translate({ vi: 'Tài liệu của tôi', en: 'My Drive', fr: 'Mon espace', es: 'Mi unidad', id: 'Drive saya', ja: 'マイドライブ', zh: '我的文档', ko: '내 드라이브', hi: 'मेरी ड्राइव', ar: 'مستنداتي' });
    }

    var activeKey = selectedNode.drive_kind + ':' + (selectedNode.code || '');

    return (
        <div className="med-explorer-panel">
            {/* Toolbar */}
            <div className="med-panel-header">
                <button className="med-tree-toggle"
                        onClick={function() { setShowTree(!showTree); }}
                        title={translate({ vi: 'Cây thư mục', en: 'Tree', fr: 'Arborescence', es: 'Árbol', id: 'Pohon folder', ja: 'ツリー', zh: '文件夹树', ko: '폴더 트리', hi: 'फ़ोल्डर ट्री', ar: 'شجرة المجلدات' })}>
                    <SvgIcon d={ICONS.tree} />
                </button>
                <button className="med-tree-toggle"
                        disabled={!canUp}
                        onClick={navUp}
                        title={translate({ vi: 'Lên thư mục cha', en: 'Up', fr: 'Remonter', es: 'Subir', id: 'Naik', ja: '上へ', zh: '上一级', ko: '상위 폴더', hi: 'ऊपर', ar: 'للأعلى' })}
                        style={{ opacity: canUp ? 1 : 0.4 }}>
                    <SvgIcon d={ICONS.up} />
                </button>
                <div className="med-breadcrumb-mini" title={breadcrumb}>{breadcrumb}</div>
                <button className="med-tree-toggle"
                        onClick={loadItems}
                        title={translate({ vi: 'Làm mới', en: 'Refresh', fr: 'Actualiser', es: 'Actualizar', id: 'Muat ulang', ja: '更新', zh: '刷新', ko: '새로고침', hi: 'रीफ़्रेश करें', ar: 'تحديث' })}>
                    <SvgIcon d={ICONS.refresh} />
                </button>
            </div>

            <div className="med-search-wrap med-panel-search">
                <SvgIcon d={ICONS.search} size={14} />
                <input type="text" className="med-search-input"
                       value={searchQuery}
                       placeholder={translate({ vi: 'Tìm trong tài liệu...', en: 'Search...', fr: 'Rechercher...', es: 'Buscar...', id: 'Cari...', ja: '検索...', zh: '搜索...', ko: '검색...', hi: 'खोजें...', ar: 'بحث...' })}
                       onChange={function(e) { setSearchQuery(e.target.value); }} />
            </div>

            <div className="med-panel-body med-panel-body-v2">
                {/* Drawer overlay từ trái — mặc định ẩn, ≡ toggle để mở */}
                <div className={'med-tree-drawer' + (showTree ? ' open' : '')}
                     onClick={function(e) { e.stopPropagation(); }}>
                    <div className="med-tree-pane">
                        <TreeGroup
                            icon={ICONS.myDrive}
                            label={translate({ vi: 'Tài liệu của tôi', en: 'My Drive', fr: 'Mon espace', es: 'Mi unidad', id: 'Drive saya', ja: 'マイドライブ', zh: '我的文档', ko: '내 드라이브', hi: 'मेरी ड्राइव', ar: 'مستنداتي' })}
                            selfKey="my:"
                            activeKey={activeKey}
                            onSelect={function() {
                                navigateTo({ drive_kind: 'my', code: null, label: 'My Drive', is_folder_subnode: false });
                                setShowTree(false);  // auto-close drawer sau khi chọn
                            }}
                        />
                        <TreeGroup
                            icon={ICONS.shared}
                            label={translate({ vi: 'Chia sẻ với tôi', en: 'Shared with me', fr: 'Partagés avec moi', es: 'Compartidos conmigo', id: 'Dibagikan kepada saya', ja: '共有アイテム', zh: '与我共享', ko: '나와 공유됨', hi: 'मेरे साथ साझा', ar: 'تمت مشاركتها معي' })}
                            selfKey="shared:"
                            activeKey={activeKey}
                            onSelect={function() {
                                navigateTo({ drive_kind: 'shared', code: null, label: 'Shared', is_folder_subnode: false });
                                setShowTree(false);
                            }}
                        />
                        <TreeGroup
                            icon={ICONS.folder}
                            label={translate({ vi: 'Theo loại chứng từ', en: 'By voucher type', fr: 'Par type de document', es: 'Por tipo de documento', id: 'Menurut jenis dokumen', ja: '伝票種別別', zh: '按单据类型', ko: '문서 유형별', hi: 'दस्तावेज़ प्रकार से', ar: 'حسب نوع المستند' })}
                            expandable
                            defaultOpen={selectedNode.drive_kind === 'voucher'}
                        >
                            {voucherTypes.map(function(vt) {
                                var label = (vt.label && translate(vt.label)) || vt.voucher_type;
                                return (
                                    <TreeLeaf
                                        key={vt.voucher_type}
                                        icon={ICONS.folder}
                                        label={label}
                                        badge={vt.file_count}
                                        selfKey={'voucher:' + vt.voucher_type}
                                        activeKey={activeKey}
                                        onSelect={function() {
                                            navigateTo({ drive_kind: 'voucher', code: vt.voucher_type, label: label, is_folder_subnode: false });
                                            setShowTree(false);
                                        }}
                                    />
                                );
                            })}
                        </TreeGroup>
                    </div>
                </div>

                {/* Backdrop — click ngoài drawer để đóng */}
                {showTree && (
                    <div className="med-tree-backdrop"
                         onClick={function() { setShowTree(false); }} />
                )}

                <div className="med-card-list">
                    {loading && <div className="med-loading">{translate({ vi: 'Đang tải...', en: 'Loading...', fr: 'Chargement...', es: 'Cargando...', id: 'Memuat...', ja: '読み込み中...', zh: '加载中...', ko: '불러오는 중...', hi: 'लोड हो रहा है...', ar: 'جارٍ التحميل...' })}</div>}
                    {!loading && items.length === 0 && (
                        <div className="med-empty">{translate({ vi: 'Không có tài liệu', en: 'No documents', fr: 'Aucun document', es: 'Sin documentos', id: 'Tidak ada dokumen', ja: '文書はありません', zh: '暂无文档', ko: '문서가 없습니다', hi: 'कोई दस्तावेज़ नहीं', ar: 'لا توجد مستندات' })}</div>
                    )}
                    {!loading && items.map(function(item) {
                        var isFolder = item.is_folder === 1 || item.is_folder === true;
                        return (
                            <div key={item.code || item.id}
                                 className={'med-card' + (isFolder ? ' folder' : '')}
                                 draggable={!isFolder}
                                 onDragStart={handleDragStart(item)}
                                 onClick={function() { handleRowClick(item); }}
                                 onDoubleClick={function() { handleRowClick(item); }}
                                 title={isFolder ? translate({ vi: 'Mở thư mục', en: 'Open folder', fr: 'Ouvrir le dossier', es: 'Abrir carpeta', id: 'Buka folder', ja: 'フォルダを開く', zh: '打开文件夹', ko: '폴더 열기', hi: 'फ़ोल्डर खोलें', ar: 'فتح المجلد' }) : translate({ vi: 'Xem trước', en: 'Preview', fr: 'Aperçu', es: 'Vista previa', id: 'Pratinjau', ja: 'プレビュー', zh: '预览', ko: '미리보기', hi: 'पूर्वावलोकन', ar: 'معاينة' })}>
                                <div className="med-card-icon med-card-icon--skeuo">
                                    <SkeuoIcon isFolder={isFolder} type={item.type} size={30} />
                                </div>
                                <div className="med-card-body">
                                    <div className="med-card-name">
                                        {item.name || item.code}
                                        {isAnalyzing(item) && (
                                            <span className="med-analyzing-inline" title={translate({ vi: 'Đang phân tích', en: 'Analyzing', fr: 'Analyse en cours', es: 'Analizando', id: 'Menganalisis', ja: '分析中', zh: '分析中', ko: '분석 중', hi: 'विश्लेषण जारी', ar: 'جارٍ التحليل' })} style={{ marginLeft: 6 }}>
                                                <AnalyzingSpinner size={11} />
                                            </span>
                                        )}
                                    </div>
                                    <div className="med-card-meta">
                                        {isFolder
                                            ? translate({ vi: 'Thư mục', en: 'Folder', fr: 'Dossier', es: 'Carpeta', id: 'Folder', ja: 'フォルダ', zh: '文件夹', ko: '폴더', hi: 'फ़ोल्डर', ar: 'مجلد' })
                                            : (item.folder_path || '') + (item.file_size ? ' · ' + formatSize(item.file_size) : '')}
                                    </div>
                                    <div className="med-card-time">{formatTime(item.updated_at || item.created_at)}</div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {viewerFile && (
                <AntFileViewer
                    visible
                    files={[{ name: viewerFile.name, url: viewerFile.full_path, type: viewerFile.sub_type || '' }]}
                    initialIndex={0}
                    onClose={function() { setViewerFile(null); }}
                />
            )}
        </div>
    );
}
